import { createClient } from '@supabase/supabase-js'
import { nanoid } from 'nanoid'

declare global {
  interface ImportMeta {
    env: {
      VITE_SUPABASE_URL?: string
      VITE_SUPABASE_ANON_KEY?: string
      VITE_SUPABASE_SERVICE_ROLE_KEY?: string
    }
  }
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const serviceKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY

// Validate Supabase configuration
const isValidUrl = (url: string): boolean => {
  try {
    new URL(url)
    return !url.includes('placeholder') && !url.includes('your-')
  } catch {
    return false
  }
}

const isValidKey = (key: string): boolean => {
  return Boolean(key) && key.length > 10 && !key.includes('placeholder') && !key.includes('your-')
}

if (!supabaseUrl || !supabaseAnonKey || !isValidUrl(supabaseUrl) || !isValidKey(supabaseAnonKey)) {
  console.error('Missing Supabase environment variables:', { 
    url: !!supabaseUrl,
    urlValid: supabaseUrl ? isValidUrl(supabaseUrl) : false,
    key: !!supabaseAnonKey,
    keyValid: supabaseAnonKey ? isValidKey(supabaseAnonKey) : false
  })
  console.error('🔧 Setup Required: Please configure your Supabase credentials in the .env file')
  console.error('📖 See SETUP_INSTRUCTIONS.md for detailed setup guide')
}

// Use safe defaults to prevent URL constructor errors
const safeSupabaseUrl = (supabaseUrl && isValidUrl(supabaseUrl)) ? supabaseUrl : 'https://placeholder.supabase.co'
const safeSupabaseAnonKey = (supabaseAnonKey && isValidKey(supabaseAnonKey)) ? supabaseAnonKey : 'placeholder-key'

// Singleton pattern to prevent multiple GoTrueClient instances
let supabaseInstance: ReturnType<typeof createClient> | null = null
let supabaseAdminInstance: ReturnType<typeof createClient> | null = null

// Suppress GoTrueClient warnings in development
const suppressGoTrueClientWarnings = () => {
  if (import.meta.env.DEV) {
    const originalWarn = console.warn;
    console.warn = (...args) => {
      const message = args[0];
      if (typeof message === 'string' && message.includes('Multiple GoTrueClient instances')) {
        return; // Suppress this specific warning
      }
      originalWarn.apply(console, args);
    };
  }
};

// Create Supabase client with singleton pattern
export const supabase = (() => {
  if (!supabaseInstance) {
    suppressGoTrueClientWarnings();
    supabaseInstance = createClient(safeSupabaseUrl, safeSupabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      }
    });
  }
  return supabaseInstance
})()

export const supabaseAdmin = (() => {
  if (!serviceKey) return null
  
  if (!supabaseAdminInstance) {
    suppressGoTrueClientWarnings();
    supabaseAdminInstance = createClient(safeSupabaseUrl, serviceKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      }
    });
  }
  return supabaseAdminInstance
})()

// Utility function to clear corrupted Supabase session
export const clearSupabaseSession = async () => {
  try {
    // Sign out from Supabase
    await supabase.auth.signOut()
    
    // Manually clear any remaining Supabase tokens from localStorage
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-')) {
        keysToRemove.push(key)
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
    })
    
    console.log('Cleared corrupted Supabase session')
  } catch (error) {
    console.error('Error clearing Supabase session:', error)
  }
}

// Utility function to check if admin client is available
export const isAdminClientAvailable = (): boolean => {
  return supabaseAdmin !== null
}

// Utility function to get the best available client (admin preferred, fallback to regular)
export const getBestAvailableClient = () => {
  return supabaseAdmin || supabase
}

// Utility function to handle RLS policy issues gracefully
export const handleRLSError = (error: any, operation: string) => {
  if (error?.code === '42501') {
    console.warn(`⚠️ RLS policy blocked ${operation} - this is expected without admin privileges`)
    console.warn('💡 Set VITE_SUPABASE_SERVICE_ROLE_KEY to enable admin operations')
    return { success: false, error: 'RLS policy blocked operation' }
  }
  return { success: false, error }
}

// Database type definitions (generated from schema)
export type Database = {
  public: {
    Tables: {
      users: {
        Row: {
          id: string
          auth_user_id: string
          email: string
          first_name: string
          last_name: string
          role: 'patient' | 'healthcare_provider' | 'lab_technician' | 'super_admin' | 'demo'
          is_active: boolean
          mfa_enabled: boolean
          encryption_key_id: string
          blockchain_address: string | null
          p2p_node_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          auth_user_id: string
          email: string
          first_name: string
          last_name: string
          role?: 'patient' | 'healthcare_provider' | 'lab_technician' | 'super_admin' | 'demo'
          is_active?: boolean
          mfa_enabled?: boolean
          encryption_key_id: string
          blockchain_address?: string | null
          p2p_node_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          auth_user_id?: string
          email?: string
          first_name?: string
          last_name?: string
          role?: 'patient' | 'healthcare_provider' | 'lab_technician' | 'super_admin' | 'demo'
          is_active?: boolean
          mfa_enabled?: boolean
          encryption_key_id?: string
          blockchain_address?: string | null
          p2p_node_id?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      user_profiles: {
        Row: {
          id: string
          user_id: string
          date_of_birth: string | null
          phone_number: string | null
          emergency_contact: string | null
          avatar_url: string | null
          preferred_providers: string[] | null
          data_storage_preference: string
          privacy_settings: any
          consent_settings: any
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          date_of_birth?: string | null
          phone_number?: string | null
          emergency_contact?: string | null
          avatar_url?: string | null
          preferred_providers?: string[] | null
          data_storage_preference?: string
          privacy_settings?: any
          consent_settings?: any
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          date_of_birth?: string | null
          phone_number?: string | null
          emergency_contact?: string | null
          avatar_url?: string | null
          preferred_providers?: string[] | null
          data_storage_preference?: string
          privacy_settings?: any
          consent_settings?: any
          created_at?: string
          updated_at?: string
        }
      }
      medical_conditions_db: {
        Row: {
          id: string
          name: string
          category: string
          icd10_code: string | null
          prevalence: string | null
          description: string | null
          keywords: string[] | null
          created_at: string
        }
      }
      user_medical_conditions: {
        Row: {
          id: string
          user_id: string
          condition_id: string | null
          custom_condition_name: string | null
          severity: string | null
          diagnosed_date: string | null
          status: string
          notes: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          condition_id?: string | null
          custom_condition_name?: string | null
          severity?: string | null
          diagnosed_date?: string | null
          status?: string
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          condition_id?: string | null
          custom_condition_name?: string | null
          severity?: string | null
          diagnosed_date?: string | null
          status?: string
          notes?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      symptoms: {
        Row: {
          id: string
          user_id: string
          name: string
          category: string
          severity: number
          notes: string | null
          triggers: string[] | null
          related_conditions: string[] | null
          ai_suggested: boolean
          recorded_at: string
          created_at: string
        }
        Insert: {
          id?: string
          user_id: string
          name: string
          category: string
          severity: number
          notes?: string | null
          triggers?: string[] | null
          related_conditions?: string[] | null
          ai_suggested?: boolean
          recorded_at: string
          created_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          name?: string
          category?: string
          severity?: number
          notes?: string | null
          triggers?: string[] | null
          related_conditions?: string[] | null
          ai_suggested?: boolean
          recorded_at?: string
          created_at?: string
        }
      }
      medications: {
        Row: {
          id: string
          user_id: string
          name: string
          dosage: string
          frequency: string
          instructions: string | null
          prescribing_provider: string | null
          start_date: string
          end_date: string | null
          is_active: boolean
          adherence_percentage: number | null
          ndc_code: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          name: string
          dosage: string
          frequency: string
          instructions?: string | null
          prescribing_provider?: string | null
          start_date: string
          end_date?: string | null
          is_active?: boolean
          adherence_percentage?: number | null
          ndc_code?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          name?: string
          dosage?: string
          frequency?: string
          instructions?: string | null
          prescribing_provider?: string | null
          start_date?: string
          end_date?: string | null
          is_active?: boolean
          adherence_percentage?: number | null
          ndc_code?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      food_entries: {
        Row: {
          id: string
          user_id: string
          meal_type: string | null
          food_name: string
          brand: string | null
          serving_size: number | null
          serving_unit: string | null
          macros: any
          usda_fdc_id: number | null
          barcode: string | null
          glucose_impact: any | null
          logged_at: string
          created_at: string
        }
        Insert: {
          id?: string
          user_id: string
          meal_type?: string | null
          food_name: string
          brand?: string | null
          serving_size?: number | null
          serving_unit?: string | null
          macros: any
          usda_fdc_id?: number | null
          barcode?: string | null
          glucose_impact?: any | null
          logged_at: string
          created_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          meal_type?: string | null
          food_name?: string
          brand?: string | null
          serving_size?: number | null
          serving_unit?: string | null
          macros?: any
          usda_fdc_id?: number | null
          barcode?: string | null
          glucose_impact?: any | null
          logged_at?: string
          created_at?: string
        }
      }
      device_data: {
        Row: {
          id: string
          device_id: string
          user_id: string
          metric_name: string
          metric_value: number
          metric_unit: string
          recorded_at: string
          source_timestamp: string | null
          metadata: any
          created_at: string
        }
        Insert: {
          id?: string
          device_id: string
          user_id: string
          metric_name: string
          metric_value: number
          metric_unit: string
          recorded_at: string
          source_timestamp?: string | null
          metadata?: any
          created_at?: string
        }
        Update: {
          id?: string
          device_id?: string
          user_id?: string
          metric_name?: string
          metric_value?: number
          metric_unit?: string
          recorded_at?: string
          source_timestamp?: string | null
          metadata?: any
          created_at?: string
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      get_user_role: {
        Args: { user_uuid: string }
        Returns: 'patient' | 'healthcare_provider' | 'lab_technician' | 'super_admin' | 'demo'
      }
      is_admin: {
        Args: { user_uuid: string }
        Returns: boolean
      }
      has_patient_access: {
        Args: { provider_uuid: string; patient_uuid: string }
        Returns: boolean
      }
      log_audit_event: {
        Args: {
          p_user_id: string
          p_action: string
          p_resource_type: string
          p_resource_id?: string
          p_details?: any
        }
        Returns: string
      }
    }
    Enums: {
      user_role: 'patient' | 'healthcare_provider' | 'lab_technician' | 'super_admin' | 'demo'
      verification_status: 'pending' | 'verified' | 'suspended'
      device_type: 'glucose_monitor' | 'heart_monitor' | 'fitness_tracker' | 'smart_scale' | 'blood_pressure' | 'pulse_oximeter'
      document_type: 'medical_record' | 'lab_result' | 'prescription' | 'consent_form' | 'insurance'
      audit_action: 'create' | 'read' | 'update' | 'delete' | 'login' | 'logout' | 'export' | 'share'
    }
  }
}

// Type aliases to simplify nested type access
export type UserRow = Database['public']['Tables']['users']['Row']
export type UserProfileRow = Database['public']['Tables']['user_profiles']['Row']

// Authentication helpers
export const signIn = async (email: string, password: string) => {
  const { error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
}

export const signUp = async (
  email: string,
  password: string,
  firstName = '',
  lastName = ''
) => {
  // Validate input
  if (!email || !email.includes('@')) {
    throw new Error('Valid email address is required')
  }
  if (!password || password.length < 6) {
    throw new Error('Password must be at least 6 characters long')
  }
  if (!firstName.trim() || !lastName.trim()) {
    throw new Error('First name and last name are required')
  }

  const { data, error } = await supabase.auth.signUp({ email, password })
  if (error) throw error

  if (data.user) {
    // Generate a strong, unique encryption_key_id for HIPAA compliance
    const encryption_key_id = nanoid(32)

    try {
      if (supabaseAdmin) {
        console.log('Creating user record with admin client...')

        const { data: userData, error: insertError } = await supabaseAdmin
          .from('users')
          .insert({
            auth_user_id: data.user.id,
            email: email.trim().toLowerCase(),
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            encryption_key_id,
            role: 'patient', // Default role
            is_active: true,
            mfa_enabled: false
          })
          .select('id')
          .single()

        if (insertError) {
          console.error('Failed to create user record:', insertError)
          throw insertError
        }

        console.log('✅ User record created successfully with ID:', userData.id)

        const { error: profileError } = await supabaseAdmin.from('user_profiles').insert({
          user_id: userData.id,
          data_storage_preference: 'hybrid',
          privacy_settings: {},
          consent_settings: {}
        })

        if (profileError) {
          console.warn('Failed to create user profile:', profileError)
        } else {
          console.log('✅ User profile created successfully')
        }
      } else {
        console.log('Admin client unavailable, using create_user_account() RPC')
        const { data: userId, error: funcError } = await supabase.rpc(
          'create_user_account',
          {
            p_auth_user_id: data.user.id,
            p_email: email.trim().toLowerCase(),
            p_first_name: firstName.trim(),
            p_last_name: lastName.trim(),
            p_encryption_key_id: encryption_key_id
          }
        )
        if (funcError) {
          console.error('create_user_account RPC failed:', funcError)
          throw funcError
        }
        console.log('✅ User created via RPC with ID:', userId)
      }
      
    } catch (insertError) {
      // Clean up auth user if user record creation fails
      console.error('User creation failed, cleaning up auth user:', insertError)
      try {
        if (supabaseAdmin) {
          await supabaseAdmin.auth.admin.deleteUser(data.user.id)
          console.log('Successfully cleaned up auth user')
        }
      } catch (cleanupError) {
        console.error('Failed to clean up auth user:', cleanupError)
      }
      
      // Provide user-friendly error messages
      if (insertError.message.includes('duplicate key')) {
        throw new Error('An account with this email already exists. Please try signing in instead.')
      } else if (insertError.message.includes('check constraint')) {
        throw new Error('Invalid data provided. Please check your information and try again.')
      } else {
        throw new Error(`Registration failed: ${insertError.message}`)
      }
    }
  }
}

export const signOutUser = async () => {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

// Password Reset Functions
export const sendPasswordResetEmail = async (email: string) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  })
  if (error) throw error
}

export const updatePassword = async (newPassword: string) => {
  const { error } = await supabase.auth.updateUser({
    password: newPassword
  })
  if (error) throw error
}

// Email Verification Functions
export const resendEmailVerification = async () => {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: (await supabase.auth.getUser()).data.user?.email || ''
  })
  if (error) throw error
}

// User Profile Management
export const updateUserProfile = async (updates: {
  first_name?: string
  last_name?: string
  email?: string
}) => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('No authenticated user')

  // Update auth user email if provided
  if (updates.email) {
    const { error: authError } = await supabase.auth.updateUser({
      email: updates.email
    })
    if (authError) throw authError
  }

  // Update user profile in database
  const { error } = await supabase
    .from('users')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('auth_user_id', user.id)

  if (error) throw error
}

// Account Deletion
export const deleteUserAccount = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('No authenticated user')

  // Mark user as inactive instead of hard delete for data integrity
  const { error } = await supabase
    .from('users')
    .update({
      is_active: false,
      updated_at: new Date().toISOString()
    })
    .eq('auth_user_id', user.id)

  if (error) throw error

  // Sign out the user
  await signOutUser()
}

// Check if user email is verified
export const isEmailVerified = async (): Promise<boolean> => {
  const { data: { user } } = await supabase.auth.getUser()
  return user?.email_confirmed_at !== null
}

// Utility functions for common database operations
export const getUserById = async (userId: string) => {
  const { data, error } = await supabase
    .from('users')
    .select(`
      *,
      user_profiles (*)
    `)
    .eq('id', userId)
    .single()
  
  if (error) {
    console.error('Error getting user by ID:', error)
    throw error
  }
  return data
}

export const getCurrentUser = async () => {
  try {
    const { data: { user: authUser } } = await supabase.auth.getUser()
    if (!authUser) {
      console.log('No authenticated user found')
      return null
    }
    
    const { data, error } = await supabase
      .from('users')
      .select(`
        *,
        user_profiles (*)
      `)
      .eq('auth_user_id', authUser.id)
      .maybeSingle()
    
    if (error) {
      console.error('Error getting current user data:', error)
      throw error
    }
    
    if (!data) {
      console.error('No user data found for auth ID:', authUser.id)
      return null
    }
    
    return data
  } catch (error) {
    console.error('Error in getCurrentUser:', error)
    throw error
  }
}

export const logAuditEvent = async (
  action: Database['public']['Enums']['audit_action'],
  resourceType: string,
  resourceId?: string,
  details?: any
) => {
  try {
    const user = await getCurrentUser()
    if (!user) {
      console.log('No user to log audit event for')
      return
    }
    
    const { data, error } = await supabase.rpc('log_audit_event', {
      p_user_id: user.id,
      p_action: action,
      p_resource_type: resourceType,
      p_resource_id: resourceId,
      p_details: details
    })
    
    if (error) {
      console.error('Audit logging failed:', error)
    }
    return data
  } catch (error) {
    console.error('Error in logAuditEvent:', error)
  }
}

// User registration diagnostic functions
export const diagnoseUserRegistration = async (email: string) => {
  try {
    console.log('=== USER REGISTRATION DIAGNOSTICS ===')
    console.log('Email:', email)
    
    // Check if user exists in auth.users
    const { data: authUser } = await supabase.auth.getUser()
    console.log('Current auth user:', authUser)
    
    // Check if user exists in public.users table
    const { data: publicUsers, error: publicError } = await supabase
      .from('users')
      .select('*')
      .eq('email', email.toLowerCase())
    
    console.log('Public users table query result:', { publicUsers, publicError })
    
    // Try with admin client as well
    if (supabaseAdmin) {
      const { data: adminUsers, error: adminError } = await supabaseAdmin
        .from('users')
        .select('*')
        .eq('email', email.toLowerCase())
      
      console.log('Admin client users table query result:', { adminUsers, adminError })
    }
    
    // Check user profiles
    if (publicUsers && publicUsers.length > 0) {
      const userId = publicUsers[0].id
      const { data: userProfiles, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
      
      console.log('User profiles query result:', { userProfiles, profileError })
    }
    
    console.log('=== END DIAGNOSTICS ===')
    
  } catch (error) {
    console.error('Diagnostic error:', error)
  }
}

// Database initialization functions
export const initializeFDAUSDADatabase = async () => {
  try {
    console.log('🗃️ Initializing FDA and USDA database tables...');
    
    // Create FDA medications table
    const fdaTableSQL = `
      CREATE TABLE IF NOT EXISTS fda_medications (
        id SERIAL PRIMARY KEY,
        ndc_code VARCHAR(20) UNIQUE NOT NULL,
        product_name TEXT NOT NULL,
        generic_name TEXT,
        manufacturer TEXT,
        dosage_form VARCHAR(100),
        route VARCHAR(100),
        strength TEXT,
        active_ingredients JSONB,
        brand_name TEXT,
        created_at TIMESTAMP DEFAULT NOW(),
        updated_at TIMESTAMP DEFAULT NOW()
      );

      CREATE INDEX IF NOT EXISTS idx_fda_ndc_code ON fda_medications(ndc_code);
      CREATE INDEX IF NOT EXISTS idx_fda_product_name ON fda_medications USING gin(to_tsvector('english', product_name));
      CREATE INDEX IF NOT EXISTS idx_fda_generic_name ON fda_medications USING gin(to_tsvector('english', generic_name));
      CREATE INDEX IF NOT EXISTS idx_fda_manufacturer ON fda_medications USING gin(to_tsvector('english', manufacturer));
      CREATE INDEX IF NOT EXISTS idx_fda_brand_name ON fda_medications USING gin(to_tsvector('english', brand_name));
    `;

    // Create USDA foods table
    const usdaTableSQL = `
      CREATE TABLE IF NOT EXISTS usda_foods (
        id SERIAL PRIMARY KEY,
        fdc_id INTEGER UNIQUE NOT NULL,
        description TEXT NOT NULL,
        brand_name TEXT,
        brand_owner TEXT,
        data_type VARCHAR(50),
        serving_size DECIMAL(10,3),
        serving_size_unit VARCHAR(20),
        nutrients JSONB,
        ingredients TEXT,
        category TEXT,
        gtin_upc VARCHAR(20),
        data_source_priority INTEGER DEFAULT 5,
        created_at TIMESTAMP DEFAULT NOW(),
        updated_at TIMESTAMP DEFAULT NOW()
      );

      CREATE INDEX IF NOT EXISTS idx_usda_fdc_id ON usda_foods(fdc_id);
      CREATE INDEX IF NOT EXISTS idx_usda_description ON usda_foods USING gin(to_tsvector('english', description));
      CREATE INDEX IF NOT EXISTS idx_usda_brand_name ON usda_foods USING gin(to_tsvector('english', brand_name));
      CREATE INDEX IF NOT EXISTS idx_usda_data_type ON usda_foods(data_type);
      CREATE INDEX IF NOT EXISTS idx_usda_gtin_upc ON usda_foods(gtin_upc) WHERE gtin_upc IS NOT NULL;
    `;

    // Create metadata table
    const metadataTableSQL = `
      CREATE TABLE IF NOT EXISTS data_import_metadata (
        id SERIAL PRIMARY KEY,
        source_name VARCHAR(100) NOT NULL UNIQUE,
        last_import_date TIMESTAMP DEFAULT NOW(),
        total_records INTEGER DEFAULT 0,
        import_status VARCHAR(20) DEFAULT 'pending',
        file_checksum TEXT,
        file_size BIGINT,
        notes TEXT,
        created_at TIMESTAMP DEFAULT NOW(),
        updated_at TIMESTAMP DEFAULT NOW()
      );
    `;

    // Execute SQL via Supabase RPC (you'd need to create this function in Supabase dashboard)
    // For now, we'll log that the tables should be created manually
    console.log('📋 Database schema prepared. Tables should be created in Supabase dashboard.');
    
    return { success: true };
  } catch (error) {
    console.error('❌ Database initialization failed:', error);
    return { success: false, error };
  }
};

export const populateUSDAFoodsSample = async () => {
  try {
    console.log('🌾 Adding sample USDA foods...');
    
    // Check if admin client is available
    if (!supabaseAdmin) {
      console.warn('⚠️ Admin client not available - using regular client with RLS bypass attempt');
      console.warn('💡 Set VITE_SUPABASE_SERVICE_ROLE_KEY to enable admin operations');
      
      // Try with regular client as fallback
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('⚠️ No authenticated user - skipping USDA food population');
        return { success: false, error: 'No authenticated user' };
      }
      
      // Try to insert with regular client (may fail due to RLS)
      const sampleFoods = [
        {
          fdc_id: 173944,
          description: 'Bread, whole wheat, commercially prepared',
          brand_name: null,
          brand_owner: null,
          data_type: 'SR Legacy',
          serving_size: 28,
          serving_size_unit: 'g',
          nutrients: [
            { nutrient_id: 1008, nutrient_name: 'Energy', amount: 247, unit: 'kcal' },
            { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 41.3, unit: 'g' },
            { nutrient_id: 1003, nutrient_name: 'Protein', amount: 12.9, unit: 'g' },
            { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 4.2, unit: 'g' },
            { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 6.0, unit: 'g' }
          ],
          ingredients: 'Whole wheat flour, water, salt, yeast',
          category: 'Baked Products',
          gtin_upc: null,
          data_source_priority: 4
        }
      ];

      const { data, error } = await supabase
        .from('usda_foods')
        .upsert(sampleFoods, { onConflict: 'fdc_id' });

      if (error) {
        console.warn('⚠️ RLS policy blocked insertion - this is expected without admin privileges');
        console.warn('💡 USDA foods will be populated when admin service role is configured');
        return { success: false, error: 'RLS policy blocked insertion' };
      }

      console.log('✅ Sample USDA foods added successfully (regular client)');
      return { success: true, count: sampleFoods.length };
    }
    
    const sampleFoods = [
      {
        fdc_id: 173944,
        description: 'Bread, whole wheat, commercially prepared',
        brand_name: null,
        brand_owner: null,
        data_type: 'SR Legacy',
        serving_size: 28,
        serving_size_unit: 'g',
        nutrients: [
          { nutrient_id: 1008, nutrient_name: 'Energy', amount: 247, unit: 'kcal' },
          { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 41.3, unit: 'g' },
          { nutrient_id: 1003, nutrient_name: 'Protein', amount: 12.9, unit: 'g' },
          { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 4.2, unit: 'g' },
          { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 6.0, unit: 'g' }
        ],
        ingredients: 'Whole wheat flour, water, salt, yeast',
        category: 'Baked Products',
        gtin_upc: null,
        data_source_priority: 4
      },
      {
        fdc_id: 168878,
        description: 'Cereals, oats, instant, fortified, plain, prepared with water',
        brand_name: null,
        brand_owner: null,
        data_type: 'SR Legacy',
        serving_size: 234,
        serving_size_unit: 'g',
        nutrients: [
          { nutrient_id: 1008, nutrient_name: 'Energy', amount: 68, unit: 'kcal' },
          { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 12, unit: 'g' },
          { nutrient_id: 1003, nutrient_name: 'Protein', amount: 2.4, unit: 'g' },
          { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 1.4, unit: 'g' },
          { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 1.7, unit: 'g' }
        ],
        ingredients: 'Oats, vitamins and minerals',
        category: 'Breakfast Cereals',
        gtin_upc: null,
        data_source_priority: 4
      },
      {
        fdc_id: 171688,
        description: 'Apple, raw, with skin',
        brand_name: null,
        brand_owner: null,
        data_type: 'Foundation',
        serving_size: 182,
        serving_size_unit: 'g',
        nutrients: [
          { nutrient_id: 1008, nutrient_name: 'Energy', amount: 95, unit: 'kcal' },
          { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 25.13, unit: 'g' },
          { nutrient_id: 1003, nutrient_name: 'Protein', amount: 0.47, unit: 'g' },
          { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 0.31, unit: 'g' },
          { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 4.4, unit: 'g' }
        ],
        ingredients: null,
        category: 'Fruits and Fruit Juices',
        gtin_upc: null,
        data_source_priority: 1
      },
      {
        fdc_id: 452201,
        description: 'Banana, raw',
        brand_name: null,
        brand_owner: null,
        data_type: 'Foundation',
        serving_size: 118,
        serving_size_unit: 'g',
        nutrients: [
          { nutrient_id: 1008, nutrient_name: 'Energy', amount: 89, unit: 'kcal' },
          { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 22.84, unit: 'g' },
          { nutrient_id: 1003, nutrient_name: 'Protein', amount: 1.09, unit: 'g' },
          { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 0.33, unit: 'g' },
          { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 2.6, unit: 'g' }
        ],
        ingredients: null,
        category: 'Fruits and Fruit Juices',
        gtin_upc: null,
        data_source_priority: 1
      },
      {
        fdc_id: 168462,
        description: 'Rice, white, long-grain, regular, cooked',
        brand_name: null,
        brand_owner: null,
        data_type: 'SR Legacy',
        serving_size: 158,
        serving_size_unit: 'g',
        nutrients: [
          { nutrient_id: 1008, nutrient_name: 'Energy', amount: 130, unit: 'kcal' },
          { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 28.17, unit: 'g' },
          { nutrient_id: 1003, nutrient_name: 'Protein', amount: 4.27, unit: 'g' },
          { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 0.44, unit: 'g' },
          { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 0.4, unit: 'g' }
        ],
        ingredients: 'Rice',
        category: 'Cereal Grains and Pasta',
        gtin_upc: null,
        data_source_priority: 4
      }
    ];

    // Use admin client to bypass RLS
    const { data, error } = await supabaseAdmin
      .from('usda_foods')
      .upsert(sampleFoods, { onConflict: 'fdc_id' });

    if (error) {
      console.error('Sample food insertion error:', error);
      return { success: false, error };
    }

    console.log('✅ Sample USDA foods added successfully');
    return { success: true, count: sampleFoods.length };
  } catch (error) {
    console.error('❌ Sample food population failed:', error);
    return { success: false, error };
  }
};

// Upsert extended profile details (phone number, DOB, emergency contact, avatar)
export const upsertUserProfileDetails = async (details: {
  phone_number?: string | null,
  date_of_birth?: string | null,
  emergency_contact?: string | null,
  avatar_url?: string | null
}) => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('No authenticated user')

  // Check if profile row exists
  const { data: existingProfile, error: fetchError } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle()

  if (fetchError) throw fetchError

  const upsertData: Partial<UserProfileRow> & { user_id: string } = {
    user_id: user.id,
    updated_at: new Date().toISOString(),
    ...details
  }

  if (!existingProfile) {
    // Insert new row
    (upsertData as any).created_at = new Date().toISOString()
  }

  const { error: upsertError } = await (supabase.from('user_profiles') as any)
    .upsert(upsertData as any)
  if (upsertError) throw upsertError
}

// ------------------------------
// Avatar Upload Helper
// ------------------------------
export const uploadUserAvatar = async (file: File): Promise<string> => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('No authenticated user')

  const fileExt = file.name.split('.').pop()
  const filePath = `${user.id}/avatar.${fileExt}`

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true,
      contentType: file.type,
    })

  if (uploadError) throw uploadError

  const { data } = supabase.storage.from('avatars').getPublicUrl(filePath)
  if (!data?.publicUrl) throw new Error('Failed to get public URL for avatar')

  // Persist avatar URL to profile
  await upsertUserProfileDetails({ avatar_url: data.publicUrl })

  return data.publicUrl
}

// ------------------------------
// MFA toggle helper
// ------------------------------
export const updateMFAEnabled = async (enabled: boolean): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('No authenticated user')

  const { error } = await supabase
    .from('users')
    .update({ mfa_enabled: enabled, updated_at: new Date().toISOString() })
    .eq('auth_user_id', user.id)

  if (error) throw error
}

// ------------------------------
// Ensure user row exists (for legacy accounts)
// ------------------------------
export const ensureUserRowExists = async (
  authUser: any,
  firstName = '',
  lastName = ''
): Promise<UserRow> => {
  // Try to fetch row first
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('auth_user_id', authUser.id)
    .maybeSingle()

  if (error) {
    console.error('Error fetching user row:', error)
    throw error
  }

  if (data) {
    console.log('User row already exists:', data.id)
    return data as UserRow
  }

  // Create minimal row using admin client to bypass RLS
  try {
    if (!supabaseAdmin) {
      throw new Error('Admin client not available for user creation')
    }
    
    // Use upsert to handle potential race conditions
    const { error: insertError, data: inserted } = await supabaseAdmin
      .from('users')
      .upsert({
        auth_user_id: authUser.id,
        email: authUser.email || '',
        first_name: firstName.trim() || 'User',
        last_name: lastName.trim() || 'Account',
        encryption_key_id: nanoid(32),
        role: 'patient',
        is_active: true,
        mfa_enabled: false
      }, {
        onConflict: 'auth_user_id',
        ignoreDuplicates: false
      })
      .select('*')
      .single()

    if (insertError) {
      // If it's a duplicate key error, try to fetch the existing row
      if (insertError.code === '23505') {
        console.log('User already exists, fetching existing row...')
        const { data: existingData, error: fetchError } = await supabaseAdmin
          .from('users')
          .select('*')
          .eq('auth_user_id', authUser.id)
          .single()
        
        if (fetchError) {
          console.error('Failed to fetch existing user after duplicate error:', fetchError)
          throw insertError
        }
        
        return existingData as UserRow
      }
      throw insertError
    }

    // Also create user profile if it doesn't exist
    try {
      const { error: profileError } = await supabaseAdmin.from('user_profiles').upsert({
        user_id: inserted.id,
        data_storage_preference: 'hybrid',
        privacy_settings: {},
        consent_settings: {}
      }, {
        onConflict: 'user_id',
        ignoreDuplicates: false
      })
      
      if (profileError && profileError.code !== '23505') {
        console.warn('Failed to create user profile during user row creation:', profileError)
      }
    } catch (profileErr) {
      console.warn('Profile creation error (may already exist):', profileErr)
    }

    return inserted as UserRow
  } catch (err) {
    console.error('Failed to create user row:', err)
    throw err
  }
}
